import { containsUnprintableOrControlCharacters } from "../containsUnprintableOrControlCharacters";

/**
 * replaces array buffers with base64. This lies that the type hasn't been changed because array buffers
 * are typed as 'any' anyway.
 * @param value Any value that be JSON-encodable if it weren't for the presence of ArrayBuffers.
 * (Since it must otherwise be JSON-encodable, sets, maps, and other values that are not JSON-encodable are not supported)
 * @returns A value where all ArrayBuffers are converted to base64 values. (however, the types are left unchanged)
 */
export const replaceArrayBuffersWithStrings = <T>(value: T, depth: number = 0, objectsSeen: Map<T, T> = new Map()): T => {
	if (typeof value === 'string' && containsUnprintableOrControlCharacters(value)) {
		// If the string contains unprintable or control characters, convert it to base64
		try {
			const b64 = btoa(value) as T;
			return b64;
		} catch { /* ignore errors */}
	}
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value == null) {
		return value;
	} else if (Array.isArray(value)) {
		if (objectsSeen.has(value)) {
			return objectsSeen.get(value) as T;
		}
		const replacement = [] as T;
		objectsSeen.set(value, replacement);
		for (const element of value) {
			(replacement as unknown[]).push(replaceArrayBuffersWithStrings(element, depth + 1, objectsSeen) as T);
		}
		return replacement;
	} else if (value instanceof ArrayBuffer) {
		try {
			const utf8 = new TextDecoder().decode(value as ArrayBuffer) as T;
			if (!containsUnprintableOrControlCharacters(utf8 as unknown as string)) {
				return utf8;
			}
		} catch { /* ignore errors */}
		const valueAsString = Array.from(new Uint8Array(value), v => String.fromCharCode(v)).join('');
		const b64 = btoa(valueAsString) as T;
		return b64;
	} else if (typeof value === 'object') {
		if (objectsSeen.has(value)) {
			return objectsSeen.get(value) as T;
		}
		const replacement = {} as T;
		objectsSeen.set(value, replacement);
		Object.assign(replacement as {}, Object.fromEntries(Object.entries(value).map(([key, value]) => [key, replaceArrayBuffersWithStrings(value, depth + 1, objectsSeen)])) as T);
		return replacement;
	}
	return value;
};